/**
 * Das Logo — EINE Stelle für alle Köpfe und Anmeldeseiten.
 *
 * Hell und dunkel liegen beide im Markup; CSS zeigt je nach `<html data-theme>`
 * eine davon (index.css, `.logo-bild--hell/--dunkel`), damit beim Umschalten
 * nichts nachlädt oder flackert. Im Dunkeln steht das Logo mit hellem
 * Schriftzug direkt auf dem dunklen Grund (Entscheidung 07.10.2026).
 *
 * Welche Dateien:
 *  - hell: das in der Verwaltung hochgeladene Logo, sonst `/logo.png`;
 *  - dunkel: das hochgeladene „Logo für dunkle Darstellung“, sonst
 *    `/logo-dunkel.png` — aber nur, solange auch das helle Logo der Standard
 *    ist. Ein eigenes helles Logo ohne dunkle Fassung steht im Dunkeln auf der
 *    hellen Scheibe (`.logo-bild`, dunkel.json): nie ein fremdes Logo.
 *
 * Cache-Marke aus der Site-Config, nicht fest eingetippt: Hier stand einmal
 * `/api/public/branding/logo?v=3`. Die Auslieferung setzt `Cache-Control:
 * immutable` — eine Marke, die sich nie ändert, heisst: Wer das Logo einmal
 * geladen hat, bekommt das neue nie zu sehen (07.08.2026: Server lieferte
 * 700×190, im Browser stand das alte 400×101). Die URLs tragen den Zeitstempel
 * des letzten Uploads.
 *
 * Vorgeladen wird nur die Fassung, die das erste Bild zeigt: React schreibt für
 * jedes `<img>` ohne `fetchPriority="low"` ein `<link rel="preload">` in den
 * Kopf. Bis 25.10.2026 standen dort beide — die zweite blieb ungenutzt, und
 * der Browser meldete es in der Konsole. Welche Fassung zuerst zu sehen ist,
 * sagt der Cookie, den das Root-Layout liest (`DarstellungAnfang`). Geladen
 * wird die andere trotzdem, nur nachrangig — nie `loading="lazy"`: Ein Bild
 * mit fester Höhe und `width: auto` ist vor dem Laden 0 px breit, und Chrome
 * lädt ein solches Bild nie (08.10.2026, Logo im Dunkeln leer).
 */
import useSiteConfig from '../hooks/useSiteConfig';
import { useDarstellungAnfang } from './DarstellungAnfang';
import { LOGO_DUNKEL, LOGO_HELL, type LogoProps } from './StandardLogo';

export default function Logo({ alt, className = '', style, onError }: LogoProps) {
  const { logoUrl, hasCustomLogo, logoDunkelUrl, hasCustomLogoDunkel } = useSiteConfig();
  const dunkelAmAnfang = useDarstellungAnfang();
  const hell = hasCustomLogo && logoUrl ? logoUrl : LOGO_HELL;
  const dunkel = hasCustomLogoDunkel && logoDunkelUrl ? logoDunkelUrl : hasCustomLogo ? null : LOGO_DUNKEL;
  if (!dunkel) {
    return <img className={`logo-bild ${className}`} src={hell} alt={alt} style={style} onError={onError} />;
  }
  return (
    <>
      <img
        className={`logo-bild logo-bild--hell ${className}`}
        src={hell}
        alt={alt}
        style={style}
        onError={onError}
        fetchPriority={dunkelAmAnfang ? 'low' : undefined}
      />
      <img
        className={`logo-bild--dunkel ${className}`}
        src={dunkel}
        alt={alt}
        style={style}
        onError={onError}
        fetchPriority={dunkelAmAnfang ? undefined : 'low'}
      />
    </>
  );
}
